import { KB_AI_PROPOSAL_KIND_LABELS, PermissionCode } from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { KbProposalCard } from '@/products/helpdesk/features/kb/kb-proposal-card';
import {
  useApplyKbProposal,
  useKbOptimization,
  useRejectKbProposal,
  useRunKbOptimization,
} from '@/products/helpdesk/features/kb/use-kb-ai';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';

export function KbOptimizePage() {
  const me = useMe();
  const canWrite = hasPermission(me.data, PermissionCode.KB_WRITE);
  const dashboard = useKbOptimization();
  const run = useRunKbOptimization();
  const apply = useApplyKbProposal();
  const reject = useRejectKbProposal();

  if (!canWrite) {
    return (
      <section className="space-y-3">
        <h1 className="text-2xl font-bold">Kein Zugriff</h1>
        <p className="text-muted-foreground">Die KB-Optimierung ist nur für Bearbeiter sichtbar.</p>
      </section>
    );
  }

  async function onRun() {
    try {
      await run.mutateAsync();
      toast.success('Analyse in die Warteschlange gelegt. Der Job läuft im Hintergrund.');
      await dashboard.refetch();
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Analyse konnte nicht gestartet werden.',
      );
    }
  }

  const data = dashboard.data;
  const running = Boolean(data?.lastRun && !data.lastRun.finishedAt);

  return (
    <section className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Button asChild variant="ghost" size="sm">
            <Link to="/helpdesk/wissen">Alle Artikel</Link>
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">KB-Optimierung</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Täglich um 02:15 (BullMQ). Suchanfragen ohne gute Treffer, gelöste Tickets ohne Artikel,
            veraltete aber oft verknüpfte Artikel. Neue Entwürfe bleiben Entwürfe, bis Sie sie
            übernehmen und veröffentlichen.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline">
            <Link to="/helpdesk/wissen/assistent">KB-Assistent</Link>
          </Button>
          <Button type="button" onClick={() => void onRun()} disabled={run.isPending || running}>
            {run.isPending || running ? (
              <>
                <Loader2Icon className="size-4 animate-spin" aria-hidden />
                Läuft …
              </>
            ) : (
              'Jetzt analysieren'
            )}
          </Button>
        </div>
      </div>

      {dashboard.isPending ? (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Auswertung wird geladen …
        </p>
      ) : null}

      {data ? (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard
              title="Suchlücken"
              value={data.searchGaps.length}
              hint="Portal, Vorschläge, Chat"
            />
            <StatCard
              title="Tickets ohne Artikel"
              value={data.ticketsWithoutArticle.length}
              hint="gelöst, 30 Tage"
            />
            <StatCard
              title="Veraltete Artikel"
              value={data.staleArticles.length}
              hint="90 Tage, neue Ticket-Links"
            />
          </div>
          <p className="text-sm text-muted-foreground">
            Letzter Lauf:{' '}
            {data.lastRun
              ? `${formatDateTime(data.lastRun.startedAt)}${data.lastRun.finishedAt ? ` · ${data.lastRun.proposalsCreated} Vorschläge` : ' · läuft'}${data.lastRun.error ? ` · ${data.lastRun.error}` : ''}`
              : 'noch keiner'}
            {data.chatAvailable ? '' : ' · Sprachmodell offline, Entwürfe kommen als Vorlage'}
          </p>

          <Card>
            <CardHeader>
              <CardTitle>Suche ohne gute Treffer</CardTitle>
              <CardDescription>
                Aus Suchlog (Volltext/Chat/Vorschläge), schwache oder leere Trefferlisten.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <GapTable
                empty="Keine Suchlücken im Zeitraum."
                rows={data.searchGaps.map((row) => [
                  row.query,
                  `${row.hits}×`,
                  formatDateTime(row.lastSeen),
                ])}
                heads={['Anfrage', 'Häufigkeit', 'Zuletzt']}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Gelöste Tickets ohne KB-Artikel</CardTitle>
              <CardDescription>Kürzlich gelöst oder geschlossen, ohne Verknüpfung.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ticket</TableHead>
                    <TableHead>Betreff</TableHead>
                    <TableHead>Kategorie</TableHead>
                    <TableHead>Gelöst</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.ticketsWithoutArticle.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-muted-foreground">
                        Keine offenen Lücken.
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.ticketsWithoutArticle.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>
                          <Link className="underline" to={`/helpdesk/tickets/${row.number}`}>
                            #{row.number}
                          </Link>
                        </TableCell>
                        <TableCell>{row.subject}</TableCell>
                        <TableCell>{row.categoryName ?? '—'}</TableCell>
                        <TableCell>{formatDateTime(row.resolvedAt)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Veraltete, aber häufig genutzte Artikel</CardTitle>
              <CardDescription>
                Lange nicht aktualisiert, verknüpft mit kürzlich gelösten Tickets.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Artikel</TableHead>
                    <TableHead>Aktualisiert</TableHead>
                    <TableHead>Neue Tickets</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.staleArticles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-muted-foreground">
                        Keine veralteten Artikel mit frischen Ticket-Links.
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.staleArticles.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>
                          <Link className="underline" to={`/helpdesk/wissen/${row.slug}`}>
                            {row.title}
                          </Link>
                        </TableCell>
                        <TableCell>{formatDateTime(row.updatedAt)}</TableCell>
                        <TableCell>{row.recentTicketCount}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="space-y-3">
            <h2 className="text-lg font-semibold">Offene Vorschläge</h2>
            {data.proposals.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Keine offenen KI-Vorschläge. Starten Sie eine Analyse oder den{' '}
                <Link className="underline" to="/helpdesk/wissen/assistent">
                  Assistenten
                </Link>
                .
              </p>
            ) : (
              data.proposals.map((proposal) => (
                <KbProposalCard
                  key={proposal.id}
                  proposal={proposal}
                  busy={apply.isPending || reject.isPending}
                  onApply={() => {
                    void apply.mutateAsync(proposal.id).then(
                      (saved) => {
                        toast.success(
                          saved.kind === 'ARTICLE_DRAFT'
                            ? `Entwurf „${saved.proposedTitle}“ angelegt (${KB_AI_PROPOSAL_KIND_LABELS[saved.kind]}).`
                            : 'Version übernommen.',
                        );
                        void dashboard.refetch();
                      },
                      (error: unknown) => {
                        toast.error(
                          error instanceof ApiRequestError
                            ? error.message
                            : 'Übernehmen fehlgeschlagen.',
                        );
                      },
                    );
                  }}
                  onReject={() => {
                    void reject.mutateAsync(proposal.id).then(
                      () => {
                        toast.success('Vorschlag verworfen.');
                        void dashboard.refetch();
                      },
                      (error: unknown) => {
                        toast.error(
                          error instanceof ApiRequestError
                            ? error.message
                            : 'Verwerfen fehlgeschlagen.',
                        );
                      },
                    );
                  }}
                />
              ))
            )}
          </div>
        </>
      ) : null}
    </section>
  );
}

function StatCard({ title, value, hint }: { title: string; value: number; hint: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle className="text-3xl">{value}</CardTitle>
      </CardHeader>
      <CardContent className="text-xs text-muted-foreground">{hint}</CardContent>
    </Card>
  );
}

function GapTable({ heads, rows, empty }: { heads: string[]; rows: string[][]; empty: string }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {heads.map((head) => (
            <TableHead key={head}>{head}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={heads.length} className="text-muted-foreground">
              {empty}
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row, index) => (
            <TableRow key={`${row[0]}-${index}`}>
              {row.map((cell) => (
                <TableCell key={cell}>{cell}</TableCell>
              ))}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
